import React, { useLayoutEffect, useState } from 'react'

import { Loading, TabList } from '@avantstay/backoffice-core'

import { EmptyMessage } from '@new/__global/components/EmptyMessage'
import { HomeownersResults } from '@new/__global/components/OmniSearch/components/ResultTemplates/HomeownersResults'
import { OmniSearchStore } from '@new/__global/components/OmniSearch/OmniSearchStore'

import useNavigationListItems from '@hooks/useNavigationListItems'

import * as S from '../OmniSearch.styles'
import { ResultTabEnum, SearchPrefix } from '../OmniSearch.types'
import {
  BookingsResults,
  PropertiesResults,
  TemplateContainer,
  UserResults,
} from './ResultTemplates'

interface OmniSearchResults {
  bookings: typeof OmniSearchStore.bookingsSearchResults
  homes: typeof OmniSearchStore.homesSearchResults
  guests: typeof OmniSearchStore.guestsSearchResults
  homeowners: typeof OmniSearchStore.homeownersSearchResults
}

interface ResultsTabsInterface {
  omniSearch: OmniSearchResults
  focusIn: string
  searchTerm: string
  isLoading?: boolean
}

const MAX_VIEWABLE_RESULTS = 10

export const renderEmptyMessage = () => (
  <S.MessageStyled>We didn&#39;t find anything, try a different search term.</S.MessageStyled>
)

function ResultsTabs({
  omniSearch,
  focusIn,
  searchTerm,
  isLoading,
}: ResultsTabsInterface): JSX.Element {
  const [tabIndex, setTabIndex] = useState(ResultTabEnum.ALL)

  const { bookings, homes, guests, homeowners } = omniSearch

  const bookingsLength = bookings?.results.length ?? 0
  const homesLength = homes?.results.length ?? 0
  const usersLength = guests?.results.length ?? 0
  const homeownersLength = homeowners?.results.length ?? 0

  const bookingTotal = bookings?.total ?? 0
  const homesTotal = homes?.total ?? 0
  const usersTotal = guests?.total ?? 0
  const homeownersTotal = homeowners?.total ?? 0

  const hasBookings = Boolean(bookingsLength)
  const hasHomes = Boolean(homesLength)
  const hasUsers = Boolean(usersLength)
  const hasHomeowners = Boolean(homeownersLength)

  const { activeIndex, setIndex } = useNavigationListItems(homesLength + bookingsLength - 1)
  const enablePropertiesNavigation = activeIndex <= homesLength - 1
  const enableBookingsNavigation = activeIndex > homesLength - 1

  const viewableBookingResults = bookings?.results.slice(0, MAX_VIEWABLE_RESULTS) ?? []
  const viewableHomeResults = homes?.results.slice(0, MAX_VIEWABLE_RESULTS) ?? []

  useLayoutEffect(() => {
    if (focusIn === SearchPrefix.PROPERTIES) {
      setTabIndex(ResultTabEnum.PROPERTIES)
    }

    if (focusIn === SearchPrefix.BOOKINGS) {
      setTabIndex(ResultTabEnum.BOOKINGS)
    }

    if (focusIn === SearchPrefix.USERS) {
      setTabIndex(ResultTabEnum.USERS)
    }

    if (focusIn === SearchPrefix.HOMEOWNERS) {
      setTabIndex(ResultTabEnum.HOMEOWNERS)
    }
  }, [focusIn])

  const onTabsChange = (index: number) => {
    setTabIndex(index)
    setIndex(-1)
  }

  const renderPropertiesResults = () => {
    return (
      <PropertiesResults
        activeIndex={activeIndex}
        enabledNavigation={enablePropertiesNavigation}
        homes={viewableHomeResults}
        setIndex={setIndex}
        totalCount={homesTotal}
        searchTerm={searchTerm}
      />
    )
  }

  const renderBookingsResults = () => {
    return (
      <BookingsResults
        activeIndex={activeIndex}
        bookings={viewableBookingResults}
        enabledNavigation={enableBookingsNavigation}
        homes={viewableHomeResults}
        setIndex={setIndex}
        totalCount={bookingTotal}
        searchTerm={searchTerm}
        linkTarget="_"
      />
    )
  }

  const renderUsersResults = () => {
    return (
      <UserResults
        activeIndex={activeIndex}
        enabledNavigation={enableBookingsNavigation}
        homes={viewableHomeResults}
        setIndex={setIndex}
        users={guests.results}
        totalCount={usersTotal}
        searchTerm={searchTerm}
      />
    )
  }

  const renderHomeownersResults = () => {
    return (
      <HomeownersResults
        homeowners={homeowners.results}
        activeIndex={activeIndex}
        setIndex={() => {}}
        totalCount={homeownersTotal}
        searchTerm={searchTerm}
      />
    )
  }

  const smallLoader = <Loading size="4px" />

  const renderCounter = (data: { isLoading: boolean; total?: number }) =>
    data.isLoading ? smallLoader : <span>{data.total}</span>

  return (
    <S.Tabs onChange={onTabsChange} index={tabIndex}>
      <TabList>
        <S.TabStyled>
          <S.TabLabel>All {isLoading ? smallLoader : null}</S.TabLabel>
        </S.TabStyled>

        <S.TabStyled disabled={!hasHomes}>
          <S.TabLabel>Properties {renderCounter(homes)}</S.TabLabel>
        </S.TabStyled>

        <S.TabStyled disabled={!hasBookings}>
          <S.TabLabel>Bookings {renderCounter(bookings)}</S.TabLabel>
        </S.TabStyled>

        <S.TabStyled disabled={!hasUsers}>
          <S.TabLabel>Guests {renderCounter(guests)}</S.TabLabel>
        </S.TabStyled>

        <S.TabStyled disabled={!hasHomeowners}>
          <S.TabLabel>Homeowners {renderCounter(homeowners)}</S.TabLabel>
        </S.TabStyled>
      </TabList>

      <S.TabPanelsStyled>
        <S.TabPanelStyled>
          {tabIndex === ResultTabEnum.ALL && (
            <>
              {hasHomes && (
                <TemplateContainer title="Properties" totalCount={homesTotal}>
                  {renderPropertiesResults()}
                </TemplateContainer>
              )}

              {hasBookings && (
                <TemplateContainer title="Bookings" totalCount={bookingTotal}>
                  {renderBookingsResults()}
                </TemplateContainer>
              )}

              {hasUsers && (
                <TemplateContainer title="Guests" totalCount={usersTotal}>
                  {renderUsersResults()}
                </TemplateContainer>
              )}

              {hasHomeowners && (
                <TemplateContainer title="Homeowners" totalCount={homesTotal}>
                  {renderHomeownersResults()}
                </TemplateContainer>
              )}

              {!hasHomes && !hasBookings && !hasUsers && !hasHomeowners ? (
                isLoading ? (
                  <Loading />
                ) : (
                  renderEmptyMessage()
                )
              ) : null}
            </>
          )}
        </S.TabPanelStyled>

        <S.TabPanelStyled>
          {tabIndex === ResultTabEnum.PROPERTIES && renderPropertiesResults()}
        </S.TabPanelStyled>

        <S.TabPanelStyled>
          {tabIndex === ResultTabEnum.BOOKINGS && renderBookingsResults()}
        </S.TabPanelStyled>

        <S.TabPanelStyled>
          {tabIndex === ResultTabEnum.USERS && renderUsersResults()}
        </S.TabPanelStyled>

        <S.TabPanelStyled>
          {tabIndex === ResultTabEnum.HOMEOWNERS && renderHomeownersResults()}
        </S.TabPanelStyled>
      </S.TabPanelsStyled>
    </S.Tabs>
  )
}

export default ResultsTabs
